import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormGroup,
  Checkbox,
  Typography,
  Box,
  CircularProgress,
  Alert
} from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';

import { ExportFormat, ExportRequest, ConversationFilters } from '../../services/conversationService';
import conversationService from '../../services/conversationService';

interface ExportDialogProps {
  open: boolean;
  onClose: () => void;
  query?: string;
  filters?: ConversationFilters;
}

const ExportDialog: React.FC<ExportDialogProps> = ({ open, onClose, query, filters }) => {
  const [format, setFormat] = useState<ExportFormat>('csv');
  const [includeMessages, setIncludeMessages] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const trimmedQuery = query?.trim();

  const handleExport = async () => {
    setLoading(true);
    setError(null);

    try {
      const exportRequest: ExportRequest = {
        format,
        ...(trimmedQuery ? { query: trimmedQuery } : {}),
        filters,
        include_messages: includeMessages
      };

      const blob = await conversationService.exportConversations(exportRequest);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Set filename based on format
      const timestamp = new Date().toISOString().slice(0, 19).replace(/[:-]/g, '');
      const extension = format === 'excel' ? 'xlsx' : format;
      const filename = `conversations_export_${timestamp}.${extension}`;
      link.download = filename;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      onClose();
    } catch (err) {
      setError('Error al exportar conversaciones. Por favor, inténtalo de nuevo.');
      console.error('Export error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <DownloadIcon />
          Exportar Conversaciones
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={3}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          
          <FormControl component="fieldset">
            <FormLabel component="legend">Formato de Exportación</FormLabel>
            <RadioGroup
              value={format}
              onChange={(e) => setFormat(e.target.value as ExportFormat)}
            >
              <FormControlLabel
                value="csv"
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      CSV (Comma Separated Values)
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Ideal para análisis en Excel o Google Sheets
                    </Typography>
                  </Box>
                }
              />
              <FormControlLabel
                value="json"
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      JSON (JavaScript Object Notation)
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Formato estructurado para integración con sistemas
                    </Typography>
                  </Box>
                }
              />
              <FormControlLabel
                value="excel"
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      Excel (Microsoft Excel)
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Hoja de cálculo `.xlsx` para análisis y seguimiento operativo
                    </Typography>
                  </Box>
                }
              />
              <FormControlLabel
                value="pdf"
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      PDF (Portable Document Format)
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Reporte visual listo para imprimir o compartir
                    </Typography>
                  </Box>
                }
              />
            </RadioGroup>
          </FormControl>

          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeMessages}
                  onChange={(e) => setIncludeMessages(e.target.checked)}
                />
              }
              label={
                <Box>
                  <Typography variant="body2">
                    Incluir contenido completo de mensajes
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Nota: Esto puede generar archivos más grandes
                  </Typography>
                </Box>
              }
            />
          </FormGroup>

          {(filters || trimmedQuery) && (
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {trimmedQuery
                  ? 'Se aplicarán la búsqueda y los filtros actuales a la exportación'
                  : 'Se aplicarán los filtros actuales a la exportación'}
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          onClick={handleExport}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : <DownloadIcon />}
        >
          {loading ? 'Exportando...' : 'Exportar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExportDialog;
